// ─────────────────────────────────────────────
//  Login.js — StockIQ Authentication Page
//  Handles: Sign In, Sign Up, Forgot Password
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./Login.css";

// Fake ticker data for the decorative tape at top
const TICKER_ITEMS = [
  { name: "RELIANCE", price: "2,945.30", change: "+1.24%", up: true },
  { name: "TCS",      price: "3,812.55", change: "+0.38%", up: true },
  { name: "INFY",     price: "1,456.80", change: "-0.61%", up: false },
  { name: "HDFCBANK", price: "1,623.45", change: "+0.95%", up: true },
  { name: "ITC",      price: "453.20",   change: "+2.10%", up: true },
  { name: "WIPRO",    price: "521.35",   change: "-1.05%", up: false },
  { name: "BAJFINANCE", price: "6,785.00", change: "+0.74%", up: true },
  { name: "NIFTY 50", price: "22,450.75", change: "+0.82%", up: true },
  { name: "SENSEX",   price: "73,812.40", change: "+0.77%", up: true },
];

export default function Login() {
  const { user, login, signup, resetPassword } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const from      = location.state?.from?.pathname || "/";

  const [tab, setTab]           = useState("signin"); // "signin" | "signup" | "reset"
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState("");
  const [loading, setLoading]   = useState(false);

  // Already logged in → redirect
  if (user) return <Navigate to={from} replace />;

  function clearMessages() { setError(""); setSuccess(""); }

  async function handleSignIn(e) {
    e.preventDefault();
    clearMessages();
    if (!email || !password) return setError("Please fill in all fields.");
    setLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  }

  async function handleSignUp(e) {
    e.preventDefault();
    clearMessages();
    if (!email || !password || !confirm) return setError("Please fill in all fields.");
    if (password !== confirm) return setError("Passwords do not match.");
    if (password.length < 6)  return setError("Password must be at least 6 characters.");
    setLoading(true);
    try {
      await signup(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e) {
    e.preventDefault();
    clearMessages();
    if (!email) return setError("Enter your email address above.");
    setLoading(true);
    try {
      await resetPassword(email);
      setSuccess("Password reset email sent! Check your inbox.");
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="siq-login-page">
      {/* Decorative background */}
      <div className="siq-login-bg" />
      <div className="siq-login-glow" />

      {/* Ticker tape */}
      <div className="siq-ticker">
        <div className="siq-ticker-inner">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
            <span className="siq-ticker-item" key={i}>
              <span className="name">{item.name}</span>
              <span className="price">{item.price}</span>
              <span className={item.up ? "up" : "down"}>
                {item.up ? "▲" : "▼"} {item.change}
              </span>
            </span>
          ))}
        </div>
      </div>

      {/* Auth Card */}
      <div className="siq-login-card" style={{ marginTop: "32px" }}>
        {/* Logo */}
        <div className="siq-login-logo">
          <div className="siq-login-logo-icon">VQ</div>
          <div className="siq-login-logo-text">VeeraStock<span>IQ</span></div>
        </div>
        <div className="siq-login-tagline">NSE &amp; BSE · Smart Analysis</div>

        {/* Tabs — only show signin / signup, not reset */}
        {tab !== "reset" && (
          <div className="siq-auth-tabs">
            <button
              className={`siq-auth-tab ${tab === "signin" ? "active" : ""}`}
              onClick={() => { setTab("signin"); clearMessages(); }}
            >
              Sign In
            </button>
            <button
              className={`siq-auth-tab ${tab === "signup" ? "active" : ""}`}
              onClick={() => { setTab("signup"); clearMessages(); }}
            >
              Create Account
            </button>
          </div>
        )}

        {/* Error / Success */}
        {error   && <div className="siq-auth-error">⚠ {error}</div>}
        {success && <div className="siq-auth-success">✓ {success}</div>}

        {/* ── Sign In Form ── */}
        {tab === "signin" && (
          <form onSubmit={handleSignIn}>
            <div className="siq-form-group">
              <label className="siq-form-label">Email</label>
              <input
                type="email"
                className="siq-form-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
            </div>
            <div className="siq-form-group">
              <label className="siq-form-label">Password</label>
              <input
                type="password"
                className="siq-form-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="siq-forgot">
              <button type="button" onClick={() => { setTab("reset"); clearMessages(); }}>
                Forgot password?
              </button>
            </div>
            <button type="submit" className="siq-btn-primary" disabled={loading}>
              {loading ? "Signing in…" : "Sign In →"}
            </button>
          </form>
        )}

        {/* ── Sign Up Form ── */}
        {tab === "signup" && (
          <form onSubmit={handleSignUp}>
            <div className="siq-form-group">
              <label className="siq-form-label">Email</label>
              <input
                type="email"
                className="siq-form-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
            </div>
            <div className="siq-form-group">
              <label className="siq-form-label">Password</label>
              <input
                type="password"
                className="siq-form-input"
                placeholder="Min. 6 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="siq-form-group">
              <label className="siq-form-label">Confirm Password</label>
              <input
                type="password"
                className="siq-form-input"
                placeholder="Repeat password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            <button type="submit" className="siq-btn-primary" disabled={loading}>
              {loading ? "Creating account…" : "Create Account →"}
            </button>
          </form>
        )}

        {/* ── Forgot Password Form ── */}
        {tab === "reset" && (
          <form onSubmit={handleReset}>
            <p style={{ color: "#64748b", fontSize: "0.82rem", marginBottom: "1rem" }}>
              Enter your email and we'll send a password reset link.
            </p>
            <div className="siq-form-group">
              <label className="siq-form-label">Email</label>
              <input
                type="email"
                className="siq-form-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
            </div>
            <button type="submit" className="siq-btn-primary" disabled={loading}>
              {loading ? "Sending…" : "Send Reset Link"}
            </button>
            <div style={{ textAlign: "center", marginTop: "0.75rem" }}>
              <button
                type="button"
                onClick={() => { setTab("signin"); clearMessages(); }}
                style={{ background: "none", border: "none", color: "#475569", fontSize: "0.8rem", cursor: "pointer", fontFamily: "inherit" }}
              >
                ← Back to Sign In
              </button>
            </div>
          </form>
        )}

        <div className="siq-login-footer">StockIQ · Personal Use Only</div>
      </div>
    </div>
  );
}

// Convert Firebase error codes to readable messages
function friendlyError(code) {
  const map = {
    "auth/user-not-found":       "No account found with this email.",
    "auth/wrong-password":       "Incorrect password. Try again.",
    "auth/email-already-in-use": "An account with this email already exists.",
    "auth/invalid-email":        "Please enter a valid email address.",
    "auth/too-many-requests":    "Too many attempts. Please try again later.",
    "auth/network-request-failed": "Network error. Check your connection.",
    "auth/invalid-credential":   "Invalid email or password.",
  };
  return map[code] || "Something went wrong. Please try again.";
}
